import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Row, Col } from "antd";
import type { CustomerDTO } from "@ifa/utils";
import styles from "../FnaSection.module.css";
import localStyles from "./FnaBasicInfoPlaceholder.module.css";
import {
  basicFnaConfig,
  type FinancialSnapshot,
  type Question,
} from "./fnaBasicConfig";
import { computeSummaries, evaluateConditions } from "./fnaBasicUtils";
import { FnaField } from "./FnaField";
import { AmountInput } from "../../../../../../components/form/AmountInput";
import { shouldShowZuuRetirementFields } from "../../../../../order-fna/zuuValidation";
import { resolveZuuFinancialInfoDisplay } from "./resolveZuuFinancialInfoDisplay";

type Props = {
  initialData?: FinancialSnapshot;
  financial?: CustomerDTO["financial"] | null;
  policyholder?: CustomerDTO["policyholder"] | null;
  isZuuFinancialStepMode?: boolean;
  onSave?: (data: FinancialSnapshot) => void;
  onEditingChange?: (editing: boolean) => void;
  mode: "view" | "edit";
};

const emptySnapshot: FinancialSnapshot = {
  income: {},
  expense: {},
  currentAssets: {},
  nonCurrentAssets: {},
  liabilities: {},
  protection: {},
};

export type FnaBasicInfoHandle = {
  save: () => FinancialSnapshot;
  cancel: () => void;
  isDirty: () => boolean;
};

export const FnaBasicInfoPlaceholder = forwardRef<FnaBasicInfoHandle, Props>(function FnaBasicInfoPlaceholder(
  {
    initialData,
    financial,
    policyholder,
    isZuuFinancialStepMode = false,
    onSave,
    onEditingChange,
    mode,
  }: Props,
  ref
) {
  const [snapshot, setSnapshot] = useState<FinancialSnapshot>(
    initialData ?? emptySnapshot
  );
  const [draft, setDraft] = useState<FinancialSnapshot>(snapshot);
  const [isDirty, setIsDirty] = useState(false);
  const isEditing = mode === "edit";

  useEffect(() => {
    setSnapshot(initialData ?? emptySnapshot);
    setDraft(initialData ?? emptySnapshot);
    setIsDirty(false);
  }, [initialData]);

  useEffect(() => {
    onEditingChange?.(isEditing && isDirty);
    return () => onEditingChange?.(false);
  }, [isEditing, isDirty, onEditingChange]);

  const computeDirty = (nextDraft: FinancialSnapshot) =>
    JSON.stringify(nextDraft) !== JSON.stringify(snapshot);

  const summaries = useMemo(() => computeSummaries(draft), [draft]);
  const zuuFinancialInfoDisplay = useMemo(
    () => resolveZuuFinancialInfoDisplay(financial),
    [financial],
  );
  const showRetirementFields = useMemo(
    () => shouldShowZuuRetirementFields(policyholder ?? undefined),
    [policyholder],
  );

  const handleValueChange = (
    question: Question,
    value: number | string | boolean | null
  ) => {
    setDraft((prev) => {
      const next: FinancialSnapshot = {
        ...prev,
        [question.section]: {
          ...(prev[question.section] as Record<string, any>),
          [question.key]: value === null ? undefined : value,
        },
      };
      setIsDirty(computeDirty(next));
      return next;
    });
  };

  const handleSave = useCallback(() => {
    setSnapshot(draft);
    onSave?.(draft);
    setIsDirty(false);
    return draft;
  }, [draft, onSave]);

  const handleCancel = useCallback(() => {
    setDraft(snapshot);
    setIsDirty(false);
  }, [snapshot]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      cancel: handleCancel,
      isDirty: () => isDirty,
    }),
    [handleSave, handleCancel, isDirty]
  );

  const renderControl = (question: Question) => {
    const value = (draft[question.section] as any)?.[question.key];
    const visible = evaluateConditions(question.visibleWhen, draft);
    if (!visible) return null;

    const isInlineSwitch =
      question.kind === "switch" && question.variant === "inlineSwitch";

    if (isInlineSwitch) {
      return (
        <Col
          span={question.colSpan ?? 24}
          key={`${question.section}-${question.key}`}
        >
          <FnaField
            question={question}
            value={value}
            mode={mode}
            onChange={(val) => handleValueChange(question, val as any)}
          />
        </Col>
      );
    }

    return (
      <Col
        span={question.colSpan ?? 12}
        className={localStyles.fieldRow}
        key={`${question.section}-${question.key}`}
      >
        <div className={localStyles.fieldLabel}>{question.label}</div>
        <FnaField
          question={question}
          value={value}
          mode={mode}
          onChange={(val) => handleValueChange(question, val as any)}
        />
      </Col>
    );
  };

  const renderZuuReadonlyQuestion = ({
    title,
    note,
    warning,
    value,
    suffix,
  }: {
    title: string;
    note: string;
    warning?: string;
    value?: number;
    suffix: string;
  }) => (
    <div className={localStyles.sectionBlock}>
      <div className={localStyles.subSectionHeader}>
        <span className={localStyles.subSectionTitle}>{title}</span>
      </div>
      <div className={localStyles.fieldRow}>
        <div className={localStyles.fieldLabel}>{note}</div>
        {warning ? <div className={localStyles.fieldLabel}>{warning}</div> : null}
        <AmountInput
          className={localStyles.fullWidthInput}
          value={value}
          readOnly
          placeholder="--"
          suffix={suffix}
          suffixVariant="box"
        />
      </div>
    </div>
  );

  if (isZuuFinancialStepMode && mode === "view") {
    return (
      <div className={`${styles.fnaCard} ${localStyles.container}`}>
        <div className={localStyles.sectionTitle}>一、財務資料</div>

        <div className={localStyles.headerBox}>
          <span>1. 可動用收入</span>
        </div>
        <div className={localStyles.sectionBlock}>
          <div className={localStyles.fieldLabel}>
            *每月可動用收入等於每月收入減去每月開支
          </div>
        </div>

        {renderZuuReadonlyQuestion({
          title:
            "1a. 在過去二十四個月裡，您從所有收入來源所得的每月平均收入為？",
          note:
            "包括薪金、花紅、佣金、其他薪酬福利、物業租賃收入、銀行存款利息、債券利息及股息等",
          value: zuuFinancialInfoDisplay.monthlyIncomeLast24Months,
          suffix: "HKD/月",
        })}

        <div className={`${localStyles.separator} ${localStyles.separatorWide}`} />

        {renderZuuReadonlyQuestion({
          title: "1b. 在過去二十四個月裡，您每月平均開支為？",
          note:
            "包括樓宇按揭、租金、衣服、交通、借貸、保險費用及保費融資產生的利息等",
          value: zuuFinancialInfoDisplay.monthlyExpenseLast24Months,
          suffix: "HKD/月",
        })}

        {showRetirementFields && (
          <>
            <div
              className={`${localStyles.separator} ${localStyles.separatorWide}`}
            />
            {renderZuuReadonlyQuestion({
              title: "1c. 於到達退休年齡後，您預期的平均每月可動用收入為？",
              note:
                "註：一般而言，退休後的可動用收入可經由不同方式獲得，例如：銀行存款及債券所產生之利息、股票股息、租金收入、從年金產品所得的收入；及/或從家庭成員或信託所得的收入等等。",
              warning: "* 不適用於退休人士",
              value:
                zuuFinancialInfoDisplay.monthlyDisposableIncomeAfterRetirement,
              suffix: "HKD/月",
            })}
          </>
        )}

        <div
          className={`${localStyles.separator} ${localStyles.separatorSection}`}
        />

        <div className={localStyles.headerBox}>
          <span>2. 流動資產</span>
        </div>
        <div className={localStyles.sectionBlock}>
          <div className={localStyles.fieldLabel}>
            *流動資產等於現時流動資產加上退休後額外流動資產
          </div>
        </div>

        {renderZuuReadonlyQuestion({
          title: "2a. 您現時累積的流動資產約有多少？",
          note:
            "註：流動資產是指可以容易變現為現金的資產。物業、藝術收藏品及藝術品均不能被視為流動資產。",
          value: zuuFinancialInfoDisplay.currentLiquidAssets,
          suffix: "HKD",
        })}

        {showRetirementFields && (
          <>
            <div
              className={`${localStyles.separator} ${localStyles.separatorWide}`}
            />
            {renderZuuReadonlyQuestion({
              title:
                "2b. 除上述2a的金額，您預期到退休年齡後額外獲得的流動資產約有多少？",
              note:
                "註：一般而言，退休後的流動資產可經由不同方式獲得，例如：強積金或退休金、銀行存款、股票或債券、遺產；及/或從保單的保證紅利/現金等。",
              warning: "* 不適用於退休人士",
              value:
                zuuFinancialInfoDisplay.additionalLiquidAssetsAfterRetirement,
              suffix: "HKD",
            })}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.fnaCard} ${localStyles.container}`}>
      {basicFnaConfig.map((section, sectionIdx) => (
        <div key={section.title}>
          <div className={localStyles.sectionTitle}>
            {section.title}
          </div>

          {section.blocks.map((block, blockIdx) => (
            <div key={block.key}>
              {block.headerSummaryKey && (
                <div className={localStyles.headerBox}>
                  <span>{block.headerLabel}</span>
                  <strong>
                    {block.headerPrefix ?? ""}
                    {summaries[block.headerSummaryKey].toLocaleString()}
                    {block.headerValueSuffix ?? ""}
                  </strong>
                </div>
              )}

              {block.groups.map((group, groupIdx) => (
                <React.Fragment key={group.key}>
                  <div className={localStyles.sectionBlock}>
                    <div className={localStyles.subSectionHeader}>
                      <span className={localStyles.subSectionTitle}>
                        {group.title}
                      </span>
                      {group.summaryKey && (
                        <span className={localStyles.subSectionSummary}>
                          {group.summaryLabel ?? block.summaryLabelPrefix ?? ""}
                          {summaries[group.summaryKey].toLocaleString()}
                          {group.summaryValueSuffix ?? ""}
                        </span>
                      )}
                    </div>
                    <Row gutter={group.gutter ?? [16, 24]}>
                      {group.questions.map((q) => renderControl(q))}
                    </Row>
                  </div>
                  {groupIdx !== block.groups.length - 1 && (
                    <div
                      className={`${localStyles.separator} ${localStyles.separatorWide}`}
                    />
                  )}
                </React.Fragment>
              ))}

              {blockIdx !== section.blocks.length - 1 && (
                <div
                  className={`${localStyles.separator} ${localStyles.separatorWide}`}
                />
              )}
            </div>
          ))}

          {sectionIdx !== basicFnaConfig.length - 1 && (
            <div
              className={`${localStyles.separator} ${localStyles.separatorSection}`}
            />
          )}
        </div>
      ))}
    </div>
  );
});
